<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Avatar from 'next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import PageHeader from 'dashboard/routes/dashboard/settings/SettingsSubPageHeader.vue';
import InboxMembersAPI from 'dashboard/api/inboxMembers';
import { facebookPageAvatarUrl } from 'custom/dashboard/helper/facebookPageAvatar';

const props = defineProps({
  pages: { type: Array, default: () => [] },
  userAccessToken: { type: String, required: true },
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const selectedIds = ref([]);
const rows = ref([]);
const phase = ref('pick');
const selectedAgentIds = ref([]);
const applying = ref(false);

const agentList = computed(() => store.getters['agents/getAgents'] || []);
const selectedAgentNames = computed(() =>
  selectedAgentIds.value.map(id => agentList.value.find(a => a.id === id)?.name ?? '')
);
const agentMenuItems = computed(() =>
  agentList.value
    .filter(({ id }) => !selectedAgentIds.value.includes(id))
    .map(({ id, name, thumbnail, avatar_url }) => ({
      label: name,
      value: id,
      action: 'select',
      thumbnail: { name, src: thumbnail || avatar_url || '' },
    }))
);

const selectablePages = computed(() => props.pages.filter(page => !page.exists));
const createCount = computed(() => selectedIds.value.length);
const failedRows = computed(() => rows.value.filter(row => row.status === 'failed'));
const successRows = computed(() =>
  rows.value.filter(row => row.status === 'success' || row.status === 'skipped')
);
const checkedSuccess = computed(() => successRows.value.filter(row => row.checked));
const allSelectableChecked = computed(
  () =>
    selectablePages.value.length > 0 &&
    selectablePages.value.every(page => selectedIds.value.includes(String(page.id)))
);

onMounted(() => {
  store.dispatch('agents/get');
});

const avatarOf = page => facebookPageAvatarUrl(page);

const togglePick = page => {
  if (page.exists || phase.value !== 'pick') return;
  const id = String(page.id);
  selectedIds.value = selectedIds.value.includes(id)
    ? selectedIds.value.filter(item => item !== id)
    : [...selectedIds.value, id];
};

const toggleSelectAll = () => {
  if (phase.value !== 'pick') return;
  selectedIds.value = allSelectableChecked.value
    ? []
    : selectablePages.value.map(page => String(page.id));
};

const toggleReview = row => {
  if (phase.value !== 'review') return;
  if (row.status !== 'success' && row.status !== 'skipped') return;
  row.checked = !row.checked;
};

const toggleReviewAll = () => {
  const next = !successRows.value.every(row => row.checked);
  successRows.value.forEach(row => {
    row.checked = next;
  });
};

const handleAgentAdd = ({ value }) => {
  if (!selectedAgentIds.value.includes(value)) selectedAgentIds.value.push(value);
};

const handleAgentRemove = index => {
  selectedAgentIds.value.splice(index, 1);
};

const pageById = id => props.pages.find(page => String(page.id) === String(id));

const createOne = async row => {
  row.status = 'creating';
  row.error = '';
  const page = row.page;
  try {
    const inbox = await store.dispatch('inboxes/createFBChannel', {
      user_access_token: props.userAccessToken,
      page_access_token: page.access_token,
      page_id: page.id,
      inbox_name: String(page.name || '').trim(),
    });
    if (!inbox?.id) throw new Error(t('CUSTOM.MESSENGER_BATCH_ADD.FAILED'));
    row.inboxId = inbox.id;
    row.status = 'success';
    row.checked = true;
  } catch (error) {
    await store.dispatch('inboxes/get');
    const found = (store.getters['inboxes/getInboxes'] || []).find(
      inbox =>
        inbox.channel_type === 'Channel::FacebookPage' &&
        String(inbox.page_id) === String(page.id)
    );
    if (found) {
      row.inboxId = found.id;
      row.status = 'skipped';
      row.checked = true;
      return;
    }
    row.status = 'failed';
    row.error = error?.message || t('CUSTOM.MESSENGER_BATCH_ADD.FAILED');
  }
};

const runCreates = async ids => {
  phase.value = 'creating';
  const pending = ids.map(id => {
    const existing = rows.value.find(row => String(row.page.id) === String(id));
    if (existing) {
      existing.status = 'queued';
      existing.error = '';
      return existing;
    }
    return {
      page: pageById(id),
      status: 'queued',
      error: '',
      inboxId: null,
      checked: false,
    };
  }).filter(row => row.page);
  const kept = rows.value.filter(row => !ids.includes(String(row.page.id)));
  rows.value = [...kept, ...pending];

  // ponytail: one Page at a time so a unique-index skip stays isolated.
  for (const row of pending) {
    // eslint-disable-next-line no-await-in-loop
    await createOne(row);
  }
  phase.value = 'review';
};

const startCreate = () => {
  if (!selectedIds.value.length) {
    useAlert(t('CUSTOM.MESSENGER_BATCH_ADD.NONE_SELECTED'));
    return;
  }
  runCreates(selectedIds.value);
};

const retryFailed = () => {
  const ids = failedRows.value.map(row => String(row.page.id));
  if (!ids.length) return;
  runCreates(ids);
};

const goToList = async () => {
  await store.dispatch('inboxes/get');
  router.replace({ name: 'settings_inbox_list' });
};

const applyAgents = async () => {
  if (!checkedSuccess.value.length) {
    useAlert(t('CUSTOM.MESSENGER_BATCH_ADD.APPLY_NEED_INBOXES'));
    return;
  }
  if (!selectedAgentIds.value.length) {
    useAlert(t('CUSTOM.MESSENGER_BATCH_ADD.APPLY_NEED_AGENTS'));
    return;
  }
  applying.value = true;
  let failed = false;
  for (const row of checkedSuccess.value) {
    if (!row.inboxId) continue;
    try {
      // eslint-disable-next-line no-await-in-loop
      await InboxMembersAPI.update({
        inboxId: row.inboxId,
        agentList: selectedAgentIds.value,
      });
    } catch {
      failed = true;
    }
  }
  applying.value = false;
  if (failed) useAlert(t('CUSTOM.MESSENGER_BATCH_ADD.APPLY_FAILED'));
  await goToList();
};

const statusLabel = status => {
  const map = {
    queued: 'CUSTOM.MESSENGER_BATCH_ADD.QUEUED',
    creating: 'CUSTOM.MESSENGER_BATCH_ADD.CREATING',
    success: 'CUSTOM.MESSENGER_BATCH_ADD.SUCCESS',
    failed: 'CUSTOM.MESSENGER_BATCH_ADD.FAILED',
    skipped: 'CUSTOM.MESSENGER_BATCH_ADD.SKIPPED',
  };
  return map[status] ? t(map[status]) : '';
};
</script>

<template>
  <div class="flex flex-col gap-4 max-w-xl">
    <PageHeader
      :header-title="t('CUSTOM.MESSENGER_BATCH_ADD.PICK_TITLE')"
      :header-content="
        phase === 'pick'
          ? t('CUSTOM.MESSENGER_BATCH_ADD.PICK_HELP')
          : t('CUSTOM.MESSENGER_BATCH_ADD.REVIEW_HELP')
      "
    />

    <div v-if="phase === 'pick'" class="flex items-center gap-3">
      <label class="inline-flex items-center gap-2 text-sm text-n-slate-12">
        <input
          type="checkbox"
          class="size-4"
          :checked="allSelectableChecked"
          :disabled="!selectablePages.length"
          @change="toggleSelectAll"
        />
        {{ t('CUSTOM.MESSENGER_BATCH_ADD.SELECT_ALL') }}
      </label>
    </div>
    <div v-else-if="phase === 'review' && successRows.length" class="flex items-center gap-3">
      <label class="inline-flex items-center gap-2 text-sm text-n-slate-12">
        <input
          type="checkbox"
          class="size-4"
          :checked="successRows.length > 0 && successRows.every(row => row.checked)"
          @change="toggleReviewAll"
        />
        {{ t('CUSTOM.MESSENGER_BATCH_ADD.SELECT_ALL') }}
      </label>
    </div>

    <ul v-if="phase === 'pick'" class="divide-y divide-n-weak rounded-xl outline outline-1 outline-n-weak">
      <li v-for="page in pages" :key="page.id">
        <label
          class="flex items-center gap-3 px-3 py-2.5"
          :class="page.exists ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'"
        >
          <input
            type="checkbox"
            class="size-4 shrink-0"
            :checked="selectedIds.includes(String(page.id))"
            :disabled="page.exists"
            @change="togglePick(page)"
          />
          <Avatar :name="page.name || page.id" :src="avatarOf(page)" :size="32" rounded-full />
          <span class="flex-1 truncate text-n-slate-12">{{ page.name }}</span>
          <span v-if="page.exists" class="text-xs text-n-slate-11 shrink-0">
            {{ t('CUSTOM.MESSENGER_BATCH_ADD.EXISTING') }}
          </span>
        </label>
      </li>
    </ul>

    <ul v-else class="divide-y divide-n-weak rounded-xl outline outline-1 outline-n-weak">
      <li v-for="row in rows" :key="row.page.id">
        <label
          class="flex items-center gap-3 px-3 py-2.5"
          :class="
            phase === 'review' && (row.status === 'success' || row.status === 'skipped')
              ? 'cursor-pointer'
              : ''
          "
        >
          <input
            v-if="phase === 'review' && (row.status === 'success' || row.status === 'skipped')"
            type="checkbox"
            class="size-4 shrink-0"
            :checked="row.checked"
            @change="toggleReview(row)"
          />
          <span v-else class="size-4 shrink-0" />
          <Avatar
            :name="row.page.name || row.page.id"
            :src="avatarOf(row.page)"
            :size="32"
            rounded-full
          />
          <span class="flex-1 min-w-0">
            <span class="block truncate text-n-slate-12">{{ row.page.name }}</span>
            <span v-if="row.error" class="block truncate text-xs text-n-ruby-11">{{ row.error }}</span>
          </span>
          <span class="text-xs text-n-slate-11 shrink-0">{{ statusLabel(row.status) }}</span>
        </label>
      </li>
    </ul>

    <div v-if="phase === 'pick'" class="flex justify-end">
      <NextButton
        :label="t('CUSTOM.MESSENGER_BATCH_ADD.CREATE', { count: createCount || 0 })"
        :disabled="!createCount"
        @click="startCreate"
      />
    </div>

    <div v-else-if="phase === 'review'" class="flex flex-col gap-4">
      <div v-if="successRows.length">
        <label class="text-n-slate-12 text-sm">
          {{ t('INBOX_MGMT.ADD.AGENTS.TITLE') }}
          <div
            class="mt-1 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong px-2 py-2"
          >
            <TagInput
              :model-value="selectedAgentNames"
              :placeholder="t('INBOX_MGMT.ADD.AGENTS.PICK_AGENTS')"
              :menu-items="agentMenuItems"
              show-dropdown
              skip-label-dedup
              @add="handleAgentAdd"
              @remove="handleAgentRemove"
            />
          </div>
        </label>
      </div>
      <div class="flex flex-wrap justify-end gap-2">
        <NextButton
          v-if="failedRows.length"
          slate
          :label="t('CUSTOM.MESSENGER_BATCH_ADD.RETRY_FAILED')"
          @click="retryFailed"
        />
        <NextButton
          slate
          :label="t('CUSTOM.MESSENGER_BATCH_ADD.SKIP')"
          @click="goToList"
        />
        <NextButton
          v-if="successRows.length"
          :is-loading="applying"
          :label="t('CUSTOM.MESSENGER_BATCH_ADD.APPLY')"
          @click="applyAgents"
        />
      </div>
    </div>
  </div>
</template>
